import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { asArray } from '../../utils/apiResponse';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: 'Admin' | 'Agent' | 'SuperAgent';
  selectedSuperAgent?: string;
  usdtWallet: string;
}

interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  phone: string;
  usdtWallet: string;
  role: 'Admin' | 'Agent' | 'SuperAgent';
  createdAt: string;
  registrationDate: string;
  createdSource: 'bot' | 'web';
  isActive?: boolean;
  parentId?: string;
  createdById?: number;
}

interface DecodedToken {
  'X-SC-Role': string;
  'X-SC-ID': string;
  'X-SC-Session-ID': string;
  'X-SC-Nonce': string;
  'X-SC-TokenType': string;
  exp: number;
  iss: string;
  aud: string;
}

const UserManagement: React.FC = () => {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    role: 'SuperAgent', // Will be updated based on user role
    usdtWallet: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentUser, setCurrentUser] = useState<string>('');
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [superAgents, setSuperAgents] = useState<User[]>([]);
  const [inviteFormExpanded, setInviteFormExpanded] = useState(true);

  // Wallet edit modal state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletLoading, setWalletLoading] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Function to fetch SuperAgent users for dropdown
  const fetchSuperAgents = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}?role=SuperAgent`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSuperAgents(data);
    } catch (error) {
      console.error('Error fetching SuperAgents:', error);
    }
  }, []);

  // Function to fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const userRole = getCurrentUserRole();
      const userId = getCurrentUserId();
      
      // Build query parameters based on user role
      const params = new URLSearchParams();
      
      if (userRole === 'Admin') {
        // Admin can filter by role
        if (roleFilter) {
          params.append('role', roleFilter);
        }
      } else if (userRole === 'SuperAgent') {
        // SuperAgent can only see Agent users under them
        params.append('role', 'Agent');
        if (userId) {
          params.append('parentId', userId);
        }
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Map backend usdcWallet to frontend usdtWallet
      const users = asArray<any>(data);
      const mappedUsers = users.map((user: any) => ({
        ...user,
        usdtWallet: user.usdcWallet || user.usdtWallet || ''
      }));
      setUsers(mappedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    } finally {
      setLoadingUsers(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const username = localStorage.getItem('username');
    
    if (token) {
      try {
        const decoded: DecodedToken = jwtDecode(token);
        // Use username if available, otherwise fall back to ID
        const displayUser = username || decoded['X-SC-ID'] || 'Unknown User';
        setCurrentUser(displayUser);
        setCurrentUserRole(decoded['X-SC-Role'] || '');
      } catch (error) {
        setCurrentUser(username || 'Unknown User');
        setCurrentUserRole('');
      }
    } else if (username) {
      setCurrentUser(username);
    }
  }, []);

  // Fetch users when component mounts or role filter changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch SuperAgents when component mounts
  useEffect(() => {
    fetchSuperAgents();
  }, [fetchSuperAgents]);

  // Set default role based on current user role
  useEffect(() => {
    if (currentUserRole === 'SuperAgent') {
      setFormData(prev => ({ ...prev, role: 'Agent' }));
    } else if (currentUserRole === 'Admin') {
      setFormData(prev => ({ ...prev, role: 'SuperAgent' }));
    }
  }, [currentUserRole]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const defaultInviteRole = (): UserFormData['role'] =>
    currentUserRole === 'SuperAgent' ? 'Agent' : 'SuperAgent';

  const resetInviteForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: defaultInviteRole(),
      selectedSuperAgent: '',
      usdtWallet: ''
    });
    setMessage(null);
  };

  const handleInviteCancel = () => {
    resetInviteForm();
    setInviteFormExpanded(false);
  };

  const collapseInvitePanel = () => {
    setInviteFormExpanded(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Prepare submission data according to new API format
      const submissionData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        usdcWallet: formData.usdtWallet, // Map frontend usdtWallet to backend usdcWallet
        createdById: formData.role === 'Agent' 
          ? (currentUserRole === 'SuperAgent' 
              ? parseInt(getCurrentUserId() || '0') 
              : parseInt(formData.selectedSuperAgent || '0'))
          : 0
      };

      const response = await fetch(API_ENDPOINTS.USERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      setMessage({ type: 'success', text: 'User created successfully!' });
      const defaultRole = currentUserRole === 'SuperAgent' ? 'Agent' : 'SuperAgent';
      setFormData({ username: '', email: '', password: '', role: defaultRole, selectedSuperAgent: '', usdtWallet: '' });
      
      // Add the new user to the local list
      const newUser: User = {
        id: Date.now(),
        username: formData.username,
        name: formData.username, // Use username as name for now
        email: formData.email,
        phone: '', // Not used anymore
        usdtWallet: formData.usdtWallet,
        role: formData.role,
        createdAt: new Date().toISOString(),
        registrationDate: new Date().toISOString(),
        createdSource: 'web',
        isActive: true,
        createdById: formData.role === 'Agent' && formData.selectedSuperAgent ? parseInt(formData.selectedSuperAgent) : undefined
      };
      setUsers(prev => [...prev, newUser]);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'An error occurred while creating the user' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const truncateWallet = (w: string) => {
    const s = (w || '').trim();
    if (!s) return '—';
    if (s.length <= 18) return s;
    return `${s.slice(0, 8)}…${s.slice(-6)}`;
  };

  const joinedLabel = (user: User) => {
    const raw = user.registrationDate || user.createdAt;
    if (!raw) return '—';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '—';
    const days = (Date.now() - date.getTime()) / 864e5;
    if (days >= 0 && days < 7) return 'Recently added';
    return formatDate(raw);
  };

  const getRoleBadge = (role: string) => {
    if (role === 'Agent') {
      return (
        <span className="badge rounded-pill" style={{ backgroundColor: '#06b6d4', color: '#fff', fontWeight: 600 }}>
          Agent
        </span>
      );
    }
    const variant = role === 'Admin' ? 'danger' : 'warning';
    return <span className={`badge bg-${variant} rounded-pill`}>{role === 'SuperAgent' ? 'Merchant' : role}</span>;
  };

  // ERC-20 wallet address validation
  const isValidEthereumAddress = (address: string): boolean => {
    // Basic Ethereum address validation (42 characters, starts with 0x, followed by 40 hex characters)
    const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
    return ethereumAddressRegex.test(address);
  };

  const handleEditUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setWalletAddress(user.usdtWallet || '');
      setShowWalletModal(true);
    }
  };

  const handleCloseWalletModal = () => {
    setShowWalletModal(false);
    setSelectedUser(null);
    setWalletAddress('');
    setMessage(null);
  };

  const handleUpdateWallet = async () => {
    if (!selectedUser || !walletAddress.trim()) {
      setMessage({ type: 'error', text: 'Please enter a wallet address' });
      return;
    }

    if (!isValidEthereumAddress(walletAddress.trim())) {
      setMessage({ type: 'error', text: 'Please enter a valid ERC-20 wallet address' });
      return;
    }

    setWalletLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}/${selectedUser.id}/address`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newWalletAddress: walletAddress.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update wallet address');
      }

      setMessage({ type: 'success', text: 'Wallet address updated successfully!' });
      
      // Update the user in the local state
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id 
          ? { ...user, usdtWallet: walletAddress.trim() }
          : user
      ));
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseWalletModal();
      }, 1500);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'An error occurred while updating the wallet address' 
      });
    } finally {
      setWalletLoading(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setMessage(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete user. Status: ${response.status}`);
      }

      // Success - remove user from list
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      setMessage({ type: 'success', text: 'User deleted successfully!' });
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseDeleteModal();
      }, 1500);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'An error occurred while deleting the user' 
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const roleScopedUsers = useMemo(() => {
    if (currentUserRole !== 'Admin' || !roleFilter.trim()) {
      return users;
    }
    const want = roleFilter.trim().toLowerCase();
    return users.filter((u) => (u.role || '').toLowerCase() === want);
  }, [users, currentUserRole, roleFilter]);

  const visibleUsersCount = roleScopedUsers.length;
  const merchantsCount = roleScopedUsers.filter((u) => u.role === 'SuperAgent').length;
  const agentsCount = roleScopedUsers.filter((u) => u.role === 'Agent').length;
  const walletsReadyCount = roleScopedUsers.filter((u) => !!u.usdtWallet).length;

  const brandBlue = '#2297D1';
  const textDark = '#0f172a';
  const textMuted = '#64748b';
  const inputStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    color: textDark,
    border: '1px solid #cbd5e1',
    borderRadius: '0.65rem',
    minHeight: '44px',
    padding: '12px'
  };

  const roleDisplayTitle = (role: string) => {
    if (role === 'SuperAgent') return 'Merchant';
    if (role === 'Agent') return 'Agent';
    if (role === 'Admin') return 'Admin';
    return role || 'Unknown';
  };

  const showInviteCard = currentUserRole === 'Admin' || currentUserRole === 'SuperAgent';

  const filteredUsers = useMemo(() => {
    const q = workspaceSearch.trim().toLowerCase();
    return roleScopedUsers.filter((u) => {
      if (!q) return true;
      return (
        (u.name || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    });
  }, [roleScopedUsers, workspaceSearch]);

  const provisioningCopy =
    currentUserRole === 'SuperAgent'
      ? 'Can create agent accounts only'
      : currentUserRole === 'Admin'
        ? 'Can create merchant and agent accounts'
        : 'View accounts in your workspace';

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 homeBg lg:px-8" style={{ backgroundColor: '#e8eaed' }}>
      <Container className="mx-auto max-w-[1440px] p-0">



        <Card
          className="mb-[40px] border-0 shadow rounded-4 blurLayerInteractive"
          style={{ backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)' }}
        >
          <Card.Body className="p-4   ">
            <p className="mb-2 text-base uppercase text-[#4A4A4A]">
              ACCESS AND PROVISIONING
            </p>
            <h1 className="fw-bold mb-2" style={{ color: textDark, fontSize: '1.75rem' }}>
              User Management
            </h1>
            <p className="mb-4" style={{ color: textMuted, maxWidth: '720px' }}>
              Create merchant and agent accounts, assign payout wallets, and keep every portfolio easy to review.
            </p>
            <Row className="g-3">
              <Col md={6} xl={3}>
                <div
                  className="rounded-3 h-100 p-3"
                  style={{ backgroundColor: '#F0F0F0', border: '1px solid #e2e8f0' }}
                >
                  <small className="text-uppercase d-block mb-1" style={{ letterSpacing: '0.12em', color: '#94a3b8', fontSize: '10px' }}>
                    Current operator
                  </small>
                  <div className="fw-semibold" style={{ color: textDark }}>{currentUser || '—'}</div>
                </div>
              </Col>
              <Col md={6} xl={3}>
                <div
                  className="rounded-3 h-100 p-3"
                  style={{ backgroundColor: '#F0F0F0', border: '1px solid #e2e8f0' }}
                >
                  <small className="text-uppercase d-block mb-1" style={{ letterSpacing: '0.12em', color: '#94a3b8', fontSize: '10px' }}>
                    Role scope
                  </small>
                  <div className="fw-semibold" style={{ color: textDark }}>{roleDisplayTitle(currentUserRole)}</div>
                </div>
              </Col>
              <Col md={6} xl={3}>
                <div
                  className="rounded-3 h-100 p-3"
                      style={{ backgroundColor: '#F0F0F0', border: '1px solid #e2e8f0' }}
                >
                  <small className="text-uppercase d-block mb-1" style={{ letterSpacing: '0.12em', color: '#94a3b8', fontSize: '10px' }}>
                    Visible users
                  </small>
                  <div className="fw-semibold" style={{ color: textDark }}>{visibleUsersCount}</div>
                </div>
              </Col>
              <Col md={6} xl={3}>
                <div
                  className="rounded-3 h-100 p-3"
                  style={{ backgroundColor: '#F0F0F0', border: '1px solid #e2e8f0' }}
                >
                  <small className="text-uppercase d-block mb-1" style={{ letterSpacing: '0.12em', color: '#94a3b8', fontSize: '10px' }}>
                    Provisioning
                  </small>
                  <div className="fw-semibold small" style={{ color: textDark }}>{provisioningCopy}</div>
                </div>
              </Col>
            </Row>
          </Card.Body>
          
        </Card>





        <div className="flex gap-[40px] flex-wrap xl:flex-nowrap">
          {showInviteCard && (
            <div className="w-full xl:flex-1">
              <Card className="border-0 shadow rounded-4 h-100 !p-0 overflow-hidden" style={{ backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)' }}>
                <div className="flex items-center justify-between   pt-[30px] px-[30px]">
                  <div>
                    <p className="mb-1 text-base uppercase text-[#4A4A4A]">INVITE ACCOUNT</p>
                    <h2 className="mb-0 text-2xl font-semibold text-slate-900">
                      Create a new agent account
                    </h2>
                  </div>
                  <button
                    type="button"
                    className="h-[37px] border-[1px] px-[13px] rounded-[6px] text-sm text-slate-700 flex-shrink-0"
                    onClick={() => (inviteFormExpanded ? collapseInvitePanel() : setInviteFormExpanded(true))}
                  >
                    {inviteFormExpanded ? 'Minimize' : 'Open invite form'}
                  </button>
                </div>
                
                {inviteFormExpanded && (
                <Card.Body className="!p-0 ">
                 <div className='border-b-[1px] pb-1 px-[30px] my-3'>
                   <p className="mb-3 text-sm sm:text-base text-slate-500">
                    Send an invite with login credentials and a payout wallet. Accounts show up in the workspace list as soon as they are created.
                  </p>

                  
                  <div className="mb-4" style={{ width: 48, height: 3, backgroundColor: brandBlue, borderRadius: 2 }} />
                 </div>

                  <div className='px-[30px] py-[24px] d-flex flex-column'>
                    <div
                    className="d-flex gap-3 mb-4 p-3 rounded-4"
                    style={{ backgroundColor: '#e8f4fc'  }}
                  >
                    <p className="mb-0 small" style={{ color: '#475569', lineHeight: 1.5 }}>
                      New accounts appear in workspace immediately after they are created, and wallet details can still be updated in the account list.
                    </p>
                  </div>

                  {message && (
                    <Alert variant={message.type === 'success' ? 'success' : 'danger'} className="mb-3">
                      {message.text}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} className="flex-grow-1 d-flex flex-column">
                    <Row className="g-3 mb-0">
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1" style={{ color: textDark, fontWeight: 600 }}>Username</Form.Label>
                          <Form.Control
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            placeholder="Username"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1" style={{ color: textDark, fontWeight: 600 }}>Email</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="name@companyemail.com"
                            required
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-3 mt-1">
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1" style={{ color: textDark, fontWeight: 600 }}>Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Password here"
                            required
                            minLength={6}
                            style={inputStyle}
                          />
                          <Form.Text className="small" style={{ color: textMuted }}>
                            At least 6 characters.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1" style={{ color: textDark, fontWeight: 600 }}>Account Type</Form.Label>
                          <Form.Select
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            required
                            style={inputStyle}
                          >
                            {currentUserRole === 'Admin' && (
                              <>
                                <option value="SuperAgent">Super Agent</option>
                                <option value="Agent">Agent</option>
                              </>
                            )}
                            {currentUserRole === 'SuperAgent' && <option value="Agent">Agent</option>}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mt-3 mb-3">
                      <Form.Label className="small mb-1" style={{ color: textDark, fontWeight: 600 }}>
                        {formData.role === 'Admin'
                          ? 'Company commission wallet (Com) — USDC Polygon'
                          : 'Merchant payout wallet (Dest) — USDC Polygon'}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="usdtWallet"
                        value={formData.usdtWallet}
                        onChange={handleInputChange}
                        placeholder="0x...."
                        required
                        style={inputStyle}
                      />
                      <Form.Text className="small" style={{ color: textMuted }}>
                        USDC on Polygon (native). Admin wallet = Com for all merchants; merchant wallet = Dest.
                      </Form.Text>
                    </Form.Group>

                    {formData.role === 'Agent' && (
                      <Form.Group className="mb-3">
                        <Form.Label className="small mb-1" style={{ color: textDark, fontWeight: 600 }}>Merchant</Form.Label>
                        {currentUserRole === 'SuperAgent' ? (
                          <>
                            <Form.Control
                              type="text"
                              value={currentUser}
                              readOnly
                              style={{ ...inputStyle, backgroundColor: '#f1f5f9', color: '#64748b' }}
                            />
                            <Form.Text className="small" style={{ color: textMuted }}>
                              This agent is tied to your merchant account ({currentUser}).
                            </Form.Text>
                          </>
                        ) : (
                          <>
                            <Form.Select
                              name="selectedSuperAgent"
                              value={formData.selectedSuperAgent || ''}
                              onChange={handleInputChange}
                              required
                              style={inputStyle}
                            >
                              <option value="">Select Super Agent</option>
                              {superAgents.map((superAgent) => (
                                <option key={superAgent.id} value={superAgent.id}>
                                  {superAgent.username} ({superAgent.email})
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Text className="small" style={{ color: textMuted }}>
                              Select the merchant (Super Agent) this agent belongs to.
                            </Form.Text>
                          </>
                        )}
                      </Form.Group>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-auto pt-3">
                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="px-4 rounded-4"
                        style={{ borderColor: '#cbd5e1', color: textDark, backgroundColor: '#fff' }}
                        onClick={handleInviteCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                        className="px-4 rounded-4"
                        style={{ backgroundColor: brandBlue, borderColor: brandBlue }}
                      >
                        {loading ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Creating…
                          </>
                        ) : (
                          'Create account'
                        )}
                      </Button>
                    </div>
                  </Form>
                  </div>
                </Card.Body>
                )}
              </Card>
            </div>
          )}
          <div className={`w-full ${showInviteCard ? 'xl:w-[560px]' : ''}`}>
            <Card className="h-100 border-0 shadow rounded-4 !p-0 overflow-hidden" style={{ backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)' }}>
              <div className="pt-[30px] px-[30px]">
                <p className="mb-1 text-base uppercase text-[#4A4A4A]">YOUR ACCESS</p>
                <h2 className="mb-0 text-2xl font-semibold text-slate-900">{roleDisplayTitle(currentUserRole)}</h2>
              </div>
              <Card.Body className="!p-0 ">
                <div className='border-b-[1px] pb-1 px-[30px] my-3'>
                  <p className="mb-3 text-sm sm:text-base text-slate-500">
                  Expand this only when you want to review your visible scope, wallet readiness and access tips.
                </p>
                <div className="mb-4" style={{ width: 48, height: 3, backgroundColor: brandBlue, borderRadius: 2 }} />
                </div>

               <div className='px-[30px] py-[24px] d-flex flex-column'>
                 <div className="text-center mb-4">
                  <p className="mb-1 text-uppercase small" style={{ letterSpacing: '0.14em', color: '#94a3b8', fontWeight: 600 }}>Logged in as</p>
                  <p className="mb-1 h4" style={{ color: brandBlue }}>{currentUser || '—'}</p>
                  <span className="small" style={{ color: textMuted }}>{roleDisplayTitle(currentUserRole)}</span>
                </div>

                <div className='rounded-4 overflow-hidden mb-4' style={{ boxShadow: '0px 0px 20px 1px #00000026' }}>
                  <div
                  className=" p-3 "
                  style={{ backgroundColor: '#f1f5f9', borderLeft: `5px solid ${brandBlue}` }}
                >
                  <div className="text-uppercase small fw-bold mb-1" style={{ color: brandBlue, letterSpacing: '0.08em', fontSize: '11px' }}>
                    VISIBLE ACCOUNTS
                  </div>
                  <div className="fw-bold" style={{ color: textDark, fontSize: '2rem', lineHeight: 1.2 }}>
                    {visibleUsersCount}
                  </div>
                  <p className="small mb-0 mt-1" style={{ color: textMuted }}>
                    Accounts currently in scope for this view.
                  </p>
                </div>
                </div>

                       <div className='rounded-4 overflow-hidden mb-4' style={{ boxShadow: '0px 0px 20px 1px #00000026' }}>
                 <div
                  className=" p-3 rounded-3"
                  style={{ backgroundColor: '#f1f5f9', borderLeft: `5px solid ${brandBlue}` }}
                >
                  <div className="text-uppercase small fw-bold mb-1" style={{ color: brandBlue, letterSpacing: '0.08em', fontSize: '11px' }}>
                    WALLETS READY
                  </div>
                  <div className="fw-bold" style={{ color: textDark, fontSize: '2rem', lineHeight: 1.2 }}>
                    {walletsReadyCount}
                  </div>
                  <p className="small mb-0 mt-1" style={{ color: textMuted }}>
                    Every visible account already has a payout wallet.
                  </p>
                </div>
               </div>

                <h3 className="fw-bold mb-2" style={{ fontSize: '1rem', color: textDark }}>What you can do here</h3>
                <ul className="ps-3 mb-0" style={{ color: textMuted, fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <li className="mb-2">Review and manage accounts in the workspace table below.</li>
                  <li>Update payout wallets or remove accounts when your role allows it.</li>
                </ul>
               </div>
              </Card.Body>
            </Card>
          </div>
        </div>

        <Row className="mt-[40px]">
          <Col>
            <Card
              className="border-0 shadow rounded-4 overflow-hidden"
              style={{ backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)' }}
            >
              <Card.Body className="!p-0">
                <div className="d-flex flex-column flex-lg-row flex-lg-wrap justify-content-between align-items-lg-start gap-3 mb-4 p-[30px] border-b-2">
                  <div>
                    <h2 className="fw-bold mb-1" style={{ color: textDark, fontSize: '24px' }}>
                      Account workspace
                    </h2>
                    <p className="mb-0 text-base" style={{ color: textMuted }}>
                      Search and filter the accounts you can manage. Refresh to load the latest from the server.
                    </p>
                  </div>
                  <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 align-items-stretch align-items-sm-center ms-lg-auto justify-content-end">
                    <Form.Control
                      type="search"
                      placeholder="Search by name or email…"
                      value={workspaceSearch}
                      onChange={(e) => setWorkspaceSearch(e.target.value)}
                      style={{ ...inputStyle, minWidth: 'min(100%, 260px)', flex: '1 1 200px' }}
                    />
                    {currentUserRole === 'Admin' && (
                      <Form.Select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        style={{ ...inputStyle, minWidth: '160px', maxWidth: '200px' }}
                      >
                        <option value="">All roles</option>
                        <option value="Admin">Admin</option>
                        <option value="SuperAgent">Merchant</option>
                        <option value="Agent">Agent</option>
                      </Form.Select>
                    )}
                    <Button
                      variant="outline-secondary"
                      className="rounded-4 text-nowrap p-3"
                      style={{ borderColor: '#cbd5e1', color: textDark, backgroundColor: '#fff' }}
                      onClick={() => fetchUsers()}
                      disabled={loadingUsers}
                    >
                      {loadingUsers ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Refresh
                        </>
                      ) : (
                        'Refresh'
                      )}
                    </Button>
                  </div>
                </div>



                <Row className="mb-4 p-[30px]">
                  {[
                    { label: 'ACCOUNTS IN VIEW', value: visibleUsersCount, desc: 'Search and role filters are already applied.' },
                    { label: 'MERCHANTS', value: merchantsCount, desc: 'Your own account is shown here when it is part of the current view.' },
                    { label: 'AGENTS', value: agentsCount, desc: 'Operator accounts that work under a merchant.' },
                  ].map((item) => (
                    <Col md={4} key={item.label} className="mb-3 mb-md-0">
                      <div
                        className="h-100 rounded-4 overflow-hidden"
                        style={{ boxShadow: '0px 0px 30.8px 2px #00000026' }}
                      >
                        <div
                          className="p-3 h-100 d-flex flex-column"
                          style={{ backgroundColor: '#f8fafc', borderLeft: `5px solid ${brandBlue}` }}
                        >
                          <div className="text-[10px] xl:text-base uppercase fw-bold mb-1" style={{ color: brandBlue, letterSpacing: '0.08em' }}>
                            {item.label}
                          </div>
                          <div className="fw-bold mb-2" style={{ color: textDark, fontSize: '1.5rem' }}>
                            {item.value}
                          </div>
                          <p className="small mb-2 mt-auto" style={{ color: textMuted }}>{item.desc}</p>
                           
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>

              <Card.Body className="px-4   pt-0 pb-4">
                {loadingUsers ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: brandBlue }} />
                    <p className="mt-2 mb-0 small" style={{ color: textMuted }}>
                      Loading users…
                    </p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-5 rounded-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="mb-0" style={{ color: textMuted }}>
                      No users found
                    </p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-5 rounded-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="mb-0" style={{ color: textMuted }}>
                      {workspaceSearch.trim()
                        ? 'No accounts match your search.'
                        : currentUserRole === 'Admin' && roleFilter
                          ? 'No accounts with this role in the current list.'
                          : 'No accounts match your filters.'}
                    </p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className="rounded-4 border p-3 p-md-4"
                        style={{ borderColor: '#e2e8f0', backgroundColor: '#fafbfc' }}
                      >
                        <div className="d-flex flex-column flex-md-row flex-md-wrap justify-content-between align-items-md-start gap-3 mb-3">
                          <div>
                            <div className="fw-bold" style={{ color: textDark, fontSize: '1.05rem' }}>
                              {user.name || user.username}
                            </div>
                            <div className="small mt-1" style={{ color: textMuted }}>
                              {user.email}
                            </div>
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            {getRoleBadge(user.role)}
                            {(currentUserRole === 'Admin' || currentUserRole === 'SuperAgent') && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="rounded-3"
                              style={{ borderColor: brandBlue, color: brandBlue, fontWeight: 600 }}
                              onClick={() => handleEditUser(user.id)}
                            >
                              Update Wallet
                            </Button>
                            )}
                            {currentUserRole === 'Admin' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="rounded-3"
                                onClick={() => handleDeleteClick(user)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                        <Row className="g-2 g-md-3">
                          <Col md={4}>
                            <div className="rounded-3 p-3 h-100" style={{ backgroundColor: '#F0F0F0' }}>
                              <div
                                className="text-uppercase mb-1"
                                style={{ fontSize: '10px', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600 }}
                              >
                                Username
                              </div>
                              <div className="small fw-medium" style={{ color: textDark, wordBreak: 'break-word' }}>
                                {user.name || user.username}
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="rounded-3 p-3 h-100" style={{ backgroundColor: '#F0F0F0' }}>
                              <div
                                className="text-uppercase mb-1"
                                style={{ fontSize: '10px', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600 }}
                              >
                                Payout wallet
                              </div>
                              <div className="small fw-medium font-monospace" style={{ color: textDark, wordBreak: 'break-all' }}>
                                {truncateWallet(user.usdtWallet || '')}
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="rounded-3 p-3 h-100" style={{ backgroundColor: '#F0F0F0' }}>
                              <div
                                className="text-uppercase mb-1"
                                style={{ fontSize: '10px', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 600 }}
                              >
                                Joined
                              </div>
                              <div className="small fw-medium" style={{ color: textDark }}>
                                {joinedLabel(user)}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>


      {/* Wallet Edit Modal */}
      <Modal show={showWalletModal} onHide={handleCloseWalletModal} centered 
      
      contentClassName="blurLayerInteractive-modal border border-slate-200/90 shadow-2xl rounded-3xl">
        <Modal.Header closeButton >
          <Modal.Title >
            Edit Wallet Address - {selectedUser?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body >
          {message && (
            <Alert variant={message.type === 'success' ? 'success' : 'danger'}>
              {message.text}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className='text-black'>
                {selectedUser?.role === 'Admin'
                  ? 'Company commission wallet (Com) — USDC Polygon'
                  : 'Merchant payout wallet (Dest) — USDC Polygon'}
              </Form.Label>
              <Form.Control
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="Enter ERC-20 wallet address (0x...)"
                                    className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-10 text-sm text-slate-900 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25"

              />
              <Form.Text style={{ color: '#6c757d' }}>
                {selectedUser?.role === 'Admin'
                  ? 'Fixed Com wallet used by every PaymentEscrow deploy.'
                  : 'Dest wallet for this merchant after claim (USDC Polygon).'}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer >
          <Button variant="secondary" onClick={handleCloseWalletModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdateWallet}
            disabled={walletLoading}
          >
            {walletLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              'Update Wallet'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered contentClassName="blurLayerInteractive-modal border border-slate-200/90 shadow-2xl rounded-3xl">
        <Modal.Header closeButton >
          <Modal.Title >
            Delete User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body >
          {message && (
            <Alert variant={message.type === 'success' ? 'success' : 'danger'}>
              {message.text}
            </Alert>
          )}
          
          <p >
            Are you sure you want to delete this user?
          </p>
          {userToDelete && (
            <div style={{ color: '#adb5bd', marginTop: '1rem' }}>
              <strong>User:</strong> {userToDelete.name} ({userToDelete.email})
            </div>
          )}
        </Modal.Body>
        <Modal.Footer >
          <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteUser}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;
